/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// ForecastGrid.jsx — ported from the source app, extended for Brand Command:
//   • Rows are grouped by Bottle Size / Pack Size with a sub-heading row per
//     group (dynamic from the user's Product SKUs) and a group subtotal row.
//   • One or many years can be shown. Each year is a block of [Jan…Dec, Total];
//     blocks sit side by side and the grid scrolls horizontally. A trailing
//     "All Yrs" column appears when more than one year is selected.
//   • Single-year + single-distributor stays fully editable (as in the source app). Showing
//     multiple years is a read-only comparison view.
import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { buildForecastView, MONTHS, MONTH_ABBR } from "../utils/forecastAggregate.js";
import "./ForecastGrid.css";

const fmt = (n) => (n > 0 ? Number(n).toLocaleString() : "—");

export default function ForecastGrid({
  selectedState,
  selectedRegion,
  selectedDistributor,
  distributorOptions,
  selectedYears = [],   // string[]
  selectedSku,          // optional — if set, shows only that SKU row
  rawForecast,
  skuNames,             // full sorted list of SKU display names
  skuToSize = {},       // { [skuName]: bottleSizeName }
  skuToPackSize = {},   // { [skuName]: packSizeName }
  onSave,               // (itemId, value) => void  — update existing record
  onDelete,             // (itemId) => void  — delete existing record
  onCreate,             // (month, skuName, value, year) => void  — create new record
  onPasteRow,           // (targetSku, { month: value }) => void — paste a copied row
  onToggleLock,         // (sku) => void — toggle lock for this row
  lockedRows,           // Set of "state|sku" keys
  saving,               // itemId currently being saved
  creating,             // "skuName|month" key currently being created
  canEdit,              // boolean — whether the current user has edit_forecast permission
}) {
  // Aggregate mode: read-only. True when no state selected, or when a state
  // has multiple distributors but none is chosen (can't target a specific row).
  const isAggregate = !selectedState || (!selectedDistributor && (distributorOptions?.length ?? 0) > 1);
  const singleYear = selectedYears.length === 1;
  const year = singleYear ? String(selectedYears[0]) : null;
  // Editing is only possible against a single, unambiguous year + row.
  const editable = singleYear && !isAggregate && canEdit;

  // Column sort
  const [sortCol, setSortCol] = useState(null);   // "sku" | "grand" | column key
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = useCallback((col) => {
    setSortCol((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return col;
    });
  }, []);

  // Column resize
  const [colWidths, setColWidths] = useState({});
  const FG_MIN_W = 40;
  const FG_DEF = { sku: 220, month: 74, total: 80, grand: 90, action: 92 };
  const startResize = useCallback((key, fallback, e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[key] ?? fallback;
    const onMove = (mv) => setColWidths((prev) => ({ ...prev, [key]: Math.max(FG_MIN_W, startW + mv.clientX - startX) }));
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [colWidths]); // eslint-disable-line react-hooks/exhaustive-deps

  // Copy/paste + cell edit state
  const [copiedRow, setCopiedRow] = useState(null);
  const [editing, setEditing] = useState(null);  // { skuIdx, monthIdx }
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  // ─── Grouped, multi-year view (identical aggregation to the export) ────────
  const view = useMemo(() => buildForecastView({
    rawForecast,
    years: selectedYears,
    selectedRegion,
    selectedState,
    selectedDistributor,
    selectedSku,
    skuNames,
    skuToSize,
    skuToPackSize,
  }), [rawForecast, selectedYears, selectedRegion, selectedState, selectedDistributor, selectedSku, skuNames, skuToSize, skuToPackSize]);

  const multiYear = view.years.length > 1;

  // backlog 214 — years the user asked for that hold no forecast records at all.
  // Without this the grid draws a complete, convincing Jan–Dec block of "—" for
  // an empty year and the "All Yrs" column quietly equals the one year that does
  // have data, so "we forecast zero" and "there is no forecast" look identical.
  const emptyYears = view.years.filter((y) => !view.yearHasData[y]);
  const allYearsEmpty = view.years.length > 0 && emptyYears.length === view.years.length;

  // ─── Column model ──────────────────────────────────────────────────────────
  const columns = useMemo(() => {
    const cols = [];
    view.years.forEach((y) => {
      MONTHS.forEach((m, mi) => cols.push({ key: `m:${y}:${m}`, kind: "month", year: y, month: m, monthIdx: mi, label: MONTH_ABBR[mi] }));
      cols.push({ key: `t:${y}`, kind: "total", year: y, label: "Total" });
    });
    if (view.years.length > 1) cols.push({ key: "grand", kind: "grand", label: "All Yrs" });
    return cols;
  }, [view.years]);

  // Value used to sort a row by the active column
  const sortValue = useCallback((row, col) => {
    if (col === "sku") return row.sku.toLowerCase();
    if (col === "grand") return row.total;
    // backlog 336 — per-year total columns are keyed "t:<year>", never the bare
    // string "total"; the only bare key is "grand".
    if (col?.startsWith("t:")) return row.yearTotals[col.slice(2)] ?? 0;
    if (col?.startsWith("m:")) {
      const [, y, m] = col.split(":");
      return row.cells[y]?.[m]?.value ?? 0;
    }
    return 0;
  }, []);

  // Rows sorted within each group (sorting never crosses group boundaries)
  const sortedGroups = useMemo(() => {
    if (!sortCol) return view.groups;
    return view.groups.map((g) => {
      const rows = [...g.rows].sort((a, b) => {
        const av = sortValue(a, sortCol);
        const bv = sortValue(b, sortCol);
        if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
        return sortDir === "asc" ? av - bv : bv - av;
      });
      return { ...g, rows };
    });
  }, [view.groups, sortCol, sortDir, sortValue]);

  // Flat sku order + lookup for editing/navigation
  const orderedSkus = useMemo(() => sortedGroups.flatMap((g) => g.rows.map((r) => r.sku)), [sortedGroups]);
  const rowBySku = useMemo(() => {
    const map = {};
    sortedGroups.forEach((g) => g.rows.forEach((r) => { map[r.sku] = r; }));
    return map;
  }, [sortedGroups]);

  // Cancel any active edit when sort / year selection changes
  useEffect(() => { setEditing(null); setEditValue(""); }, [sortCol, sortDir, year]);

  // ─── Editing helpers (single-year only) ────────────────────────────────────
  const startEdit = useCallback((skuIdx, monthIdx) => {
    const sku = orderedSkus[skuIdx];
    const cell = rowBySku[sku]?.cells[year]?.[MONTHS[monthIdx]];
    setEditing({ skuIdx, monthIdx });
    setEditValue(cell?.value != null ? String(cell.value) : "");
  }, [orderedSkus, rowBySku, year]);

  const cancelEdit = useCallback(() => { setEditing(null); setEditValue(""); }, []);

  const copyRow = useCallback((sku) => {
    const cells = rowBySku[sku]?.cells[year] || {};
    const monthValues = {};
    MONTHS.forEach((m) => { if (cells[m]?.value != null) monthValues[m] = cells[m].value; });
    setCopiedRow({ sku, monthValues });
  }, [rowBySku, year]);

  const pasteRow = useCallback((targetSku) => {
    if (!copiedRow || !editable) return;
    onPasteRow(targetSku, copiedRow.monthValues);
  }, [copiedRow, editable, onPasteRow]);

  const commitEdit = useCallback((nextSkuIdx = null, nextMonthIdx = null) => {
    if (!editing) return;
    const { skuIdx, monthIdx } = editing;
    const sku = orderedSkus[skuIdx];
    const month = MONTHS[monthIdx];
    const parsed = parseFloat(editValue);

    setEditing(null);
    setEditValue("");

    const cell = rowBySku[sku]?.cells[year]?.[month];
    const isEmpty = editValue.trim() === "";

    if (isEmpty) {
      if (cell?.id) onDelete(cell.id);
    } else if (!isNaN(parsed) && parsed > 0) {
      if (cell?.id) {
        if (parsed !== cell.value) onSave(cell.id, parsed);
      } else {
        onCreate(month, sku, parsed, year);
      }
    }

    if (nextSkuIdx != null && nextMonthIdx != null) {
      setTimeout(() => startEdit(nextSkuIdx, nextMonthIdx), 0);
    }
  }, [editing, editValue, orderedSkus, rowBySku, year, onSave, onDelete, onCreate, startEdit]);

  const handleKeyDown = useCallback((e) => {
    if (!editing) return;
    const { skuIdx, monthIdx } = editing;
    const lastSku = orderedSkus.length - 1;
    const lastMonth = 11;

    if (e.key === "Tab") {
      e.preventDefault();
      if (!e.shiftKey) {
        const nextMonth = monthIdx < lastMonth ? monthIdx + 1 : 0;
        const nextSku = monthIdx < lastMonth ? skuIdx : (skuIdx < lastSku ? skuIdx + 1 : 0);
        commitEdit(nextSku, nextMonth);
      } else {
        const nextMonth = monthIdx > 0 ? monthIdx - 1 : lastMonth;
        const nextSku = monthIdx > 0 ? skuIdx : (skuIdx > 0 ? skuIdx - 1 : lastSku);
        commitEdit(nextSku, nextMonth);
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      const nextSku = skuIdx < lastSku ? skuIdx + 1 : 0;
      commitEdit(nextSku, monthIdx);
    } else if (e.key === "Escape") {
      cancelEdit();
    }
  }, [editing, orderedSkus.length, commitEdit, cancelEdit]);

  // ─── Empty / not-ready state ───────────────────────────────────────────────
  if (selectedYears.length === 0) {
    return (
      <div className="grid-prompt">
        <div className="grid-prompt-icon">📋</div>
        <div className="grid-prompt-text">
          Select a <strong>Year</strong> to view forecasts.
          {canEdit
            ? <> Add a <strong>State</strong> to enable editing.</>
            : <> Contact an admin to get forecast edit access.</>}
        </div>
      </div>
    );
  }

  // No SKUs at all → the grid would be headers over nothing, and the "no
  // forecast" message below would be a dead end (there is nowhere to type).
  // Name the real cause instead. (Found while fixing backlog 214.)
  if (skuNames.length === 0) {
    return (
      <div className="grid-prompt">
        <div className="grid-prompt-icon">📦</div>
        <div className="grid-prompt-text">
          No <strong>Product SKUs</strong> are set up yet, so there is nothing to forecast.
          Add them in <strong>System Settings</strong> first.
        </div>
      </div>
    );
  }

  // backlog 214 — nothing forecast for ANY selected year, and this view can't be
  // typed into (aggregate, multi-year, or no edit permission). A read-only wall
  // of em-dashes tells the user nothing, so say it outright.
  if (allYearsEmpty && !editable) {
    return (
      <div className="grid-prompt">
        <div className="grid-prompt-icon">📭</div>
        <div className="grid-prompt-text">
          No forecast has been entered for <strong>{view.years.join(" · ")}</strong>.
          {canEdit
            ? <> Pick a single <strong>Year</strong> plus a <strong>State</strong> and <strong>Distributor</strong> to start one.</>
            : <> Contact an admin to get forecast edit access.</>}
        </div>
      </div>
    );
  }

  const showAction = editable; // copy/paste/lock column only in editable mode
  const colCount = 1 + columns.length + (showAction ? 1 : 0);

  const sortIndicator = (col) => {
    if (sortCol !== col) return <span className="sort-icon sort-icon-idle">↕</span>;
    return <span className="sort-icon">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  const colWidth = (col) => colWidths[col.key] ?? FG_DEF[col.kind];

  return (
    <div className="grid-wrap">
      <div className="grid-unit-header">
        Forecast in Physical Cases
        {multiYear && <span className="grid-unit-years"> · {view.years.join(" · ")}</span>}
      </div>
      {/* backlog 214 — the grid below still renders the empty year's columns (it
          has to: the other year needs its side-by-side block, and in the single
          editable year the blank cells ARE the entry surface). Say what those
          em-dashes mean before the user reads them as zeros, and admit that the
          "All Yrs" column is not covering the year(s) named here. */}
      {emptyYears.length > 0 && (
        <div className="grid-empty-years-note" role="status">
          No forecast entered for {emptyYears.join(", ")}
          {multiYear
            ? ` — those columns are empty and "All Yrs" reflects only the other selected year${view.years.length - emptyYears.length === 1 ? "" : "s"}.`
            : " — click a cell to start one."}
        </div>
      )}
      <div className="grid-scroll">
        <table className="forecast-grid" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: colWidths.sku ?? FG_DEF.sku }} />
            {columns.map((c) => <col key={c.key} style={{ width: colWidth(c) }} />)}
            {showAction && <col style={{ width: colWidths.action ?? FG_DEF.action }} />}
          </colgroup>

          <thead>
            {/* Year super-header (only when comparing multiple years).
                NOTE: no rowSpan — sticky positioning renders incorrectly on
                row-spanning cells (the header overhangs the first body row).
                Instead the "Product SKU" / "All Yrs" labels live in this top
                row and the second row gets blank placeholder cells beneath
                them, so every header cell is single-height and sticks cleanly. */}
            {multiYear && (
              <tr className="grid-head-super">
                <th className="grid-th grid-sku-th fg-sortable-th" onClick={() => handleSort("sku")}>
                  Product SKU{sortIndicator("sku")}
                </th>
                {view.years.map((y) => {
                  // backlog 214 — label the empty year in its own header and drop
                  // the emphasis tint, so a block of "—" can't be misread as zeros.
                  const yearEmpty = !view.yearHasData[y];
                  return (
                    <th key={y} className={`grid-th grid-year-th${yearEmpty ? " grid-year-th-empty" : ""}`} colSpan={13}>
                      {y}
                      {yearEmpty && <span className="grid-year-empty-tag">no forecast</span>}
                    </th>
                  );
                })}
                <th className="grid-th grid-grand-th fg-sortable-th" onClick={() => handleSort("grand")}>
                  All Yrs{sortIndicator("grand")}
                </th>
              </tr>
            )}
            <tr className={multiYear ? "grid-head-sub" : undefined}>
              {multiYear ? (
                <th className="grid-th grid-sku-th grid-head-blank" aria-hidden />
              ) : (
                <th className="grid-th grid-sku-th fg-sortable-th" onClick={() => handleSort("sku")}>
                  Product SKU{sortIndicator("sku")}
                  <span className="col-resize-handle" onMouseDown={(e) => startResize("sku", FG_DEF.sku, e)} />
                </th>
              )}
              {columns.map((c) => {
                if (c.kind === "grand") {
                  // "All Yrs" label is in the super-row; keep the column aligned.
                  return <th key={c.key} className="grid-th grid-grand-col-th grid-head-blank" aria-hidden />;
                }
                const cls = c.kind === "total" ? "grid-th grid-total-th fg-sortable-th" : "grid-th grid-month-th fg-sortable-th";
                return (
                  <th key={c.key} className={cls} onClick={() => handleSort(c.key)}>
                    {c.label}{sortIndicator(c.key)}
                    <span className="col-resize-handle" onMouseDown={(e) => startResize(c.key, colWidth(c), e)} />
                  </th>
                );
              })}
              {showAction && <th className="grid-th grid-action-th" />}
            </tr>
          </thead>

          <tbody>
            {sortedGroups.map((g) => {
              let rowsBefore = 0;
              // index offset of this group's first row within orderedSkus
              for (const gg of sortedGroups) { if (gg === g) break; rowsBefore += gg.rows.length; }
              return (
                <React.Fragment key={g.label}>
                  {/* ── Sub-heading: Bottle Size / Pack Size ── */}
                  <tr className="grid-group-row">
                    <td className="grid-group-cell" colSpan={colCount}>
                      <span className="grid-group-inner">
                        <span className="grid-group-dot" aria-hidden />
                        <span className="grid-group-text">{g.label}</span>
                        <span className="grid-group-count">{g.rows.length} SKU{g.rows.length === 1 ? "" : "s"}</span>
                      </span>
                    </td>
                  </tr>

                  {g.rows.map((row, idxInGroup) => {
                    const sku = row.sku;
                    const skuIdx = rowsBefore + idxInGroup;
                    const isLocked = editable && !!lockedRows?.has(`${selectedState}|${sku}`);

                    return (
                      <tr key={sku} className={`${skuIdx % 2 === 0 ? "grid-row" : "grid-row grid-row-alt"}${isLocked ? " grid-row-locked" : ""}`}>
                        <td className="grid-sku-cell" title={sku}>{sku}</td>

                        {columns.map((col) => {
                          if (col.kind === "total") {
                            return <td key={col.key} className="grid-row-total">{fmt(row.yearTotals[col.year])}</td>;
                          }
                          if (col.kind === "grand") {
                            return <td key={col.key} className="grid-row-total grid-row-grand">{fmt(row.total)}</td>;
                          }
                          // month cell
                          const cell = row.cells[col.year]?.[col.month];
                          const isEditing = editable && editing?.skuIdx === skuIdx && editing?.monthIdx === col.monthIdx;
                          const isSaving = cell?.id && saving === cell.id;
                          const isCreating = creating === `${sku}|${col.month}`;
                          const isEmpty = !cell;
                          const editorTip = cell?.modifiedBy
                            ? `${cell.modifiedBy}${cell.modified ? " · " + new Date(cell.modified).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}`
                            : null;
                          const isReadonly = !editable || isLocked;

                          return (
                            <td
                              key={col.key}
                              className={`grid-data-cell${isEmpty ? " grid-cell-empty" : ""}${isReadonly ? " grid-cell-readonly" : ""}${editorTip && !isLocked && editable ? " grid-cell-has-editor" : ""}`}
                              onClick={() => { if (!isEditing && !isReadonly) startEdit(skuIdx, col.monthIdx); }}
                              data-editor={editorTip || undefined}
                            >
                              {isEditing ? (
                                <input
                                  ref={inputRef}
                                  type="number"
                                  className="grid-input"
                                  value={editValue}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setEditValue(v === "" ? "" : String(parseInt(v, 10) || ""));
                                  }}
                                  onKeyDown={handleKeyDown}
                                  onBlur={() => commitEdit()}
                                  min={0}
                                />
                              ) : (isSaving || isCreating) ? (
                                <span className="grid-spinner">·</span>
                              ) : (
                                <span className={`grid-val${isEmpty ? " grid-val-empty" : ""}`}>
                                  {cell?.value != null ? Number(cell.value).toLocaleString() : "—"}
                                </span>
                              )}
                            </td>
                          );
                        })}

                        {showAction && (
                          <td className={`grid-action-cell${copiedRow?.sku === sku ? " grid-row-copied" : ""}`}>
                            <button className="grid-copy-btn" onClick={() => copyRow(sku)} title="Copy this row">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                            </button>
                            {copiedRow && copiedRow.sku !== sku && !isLocked && (
                              <button className="grid-paste-btn" onClick={() => pasteRow(sku)} title={`Paste from ${copiedRow.sku}`}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                                </svg>
                              </button>
                            )}
                            <button
                              className={`grid-lock-btn${isLocked ? " grid-lock-btn-active" : ""}`}
                              onClick={() => onToggleLock(sku)}
                              title={isLocked ? "Unlock this row" : "Lock this row"}
                            >
                              {isLocked ? (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                              ) : (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" />
                                </svg>
                              )}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* ── Subtotals by Bottle Size / Pack Size — collected at the bottom,
                   just above the grand total, fenced by colored separator lines. ── */}
            {sortedGroups.length > 0 && (
              <>
                <tr className="grid-sep-row grid-sep-forecast"><td colSpan={colCount} /></tr>
                <tr className="grid-subtotal-head">
                  {/* backlog 215 — the label must live in a sticky INNER span, exactly
                      like .grid-group-inner above. `position:sticky; left:0` on the
                      colSpan cell itself is a no-op (the cell already starts at x=0
                      and spans the full table), so at two years / ~2,250px the text
                      scrolled away and left an empty orange stripe. */}
                  <td className="grid-subtotal-head-label" colSpan={colCount}>
                    <span className="grid-subtotal-head-inner">Subtotals by Bottle Size / Pack Size</span>
                  </td>
                </tr>
                {sortedGroups.map((g) => (
                  <tr className="grid-subtotal-row" key={`sub-${g.label}`}>
                    <td className="grid-subtotal-label" title={g.label}>{g.label}</td>
                    {columns.map((col) => {
                      if (col.kind === "total") return <td key={col.key} className="grid-subtotal-cell">{fmt(g.groupYearTotals[col.year])}</td>;
                      if (col.kind === "grand") return <td key={col.key} className="grid-subtotal-cell">{fmt(g.groupTotal)}</td>;
                      return <td key={col.key} className="grid-subtotal-cell">{fmt(g.groupYM[col.year]?.[col.month] ?? 0)}</td>;
                    })}
                    {showAction && <td className="grid-subtotal-cell" />}
                  </tr>
                ))}
                <tr className="grid-sep-row grid-sep-total"><td colSpan={colCount} /></tr>
              </>
            )}
          </tbody>

          <tfoot>
            <tr className="grid-footer-row">
              <td className="grid-footer-label">Total</td>
              {columns.map((col) => {
                if (col.kind === "total") return <td key={col.key} className="grid-footer-cell grid-footer-yeartotal">{fmt(view.colYearTotals[col.year])}</td>;
                if (col.kind === "grand") return <td key={col.key} className="grid-footer-grand">{fmt(view.grandTotal)}</td>;
                return <td key={col.key} className="grid-footer-cell">{fmt(view.colYM[col.year]?.[col.month] ?? 0)}</td>;
              })}
              {showAction && <td className="grid-footer-grand" />}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
